<template>
    <div>
        <app-template>
            <!-- Header -->
            <div class="mb-6">
                <h1 class="text-2xl font-semibold text-gray-900 mb-1">
                    Références des appareils
                </h1>
                <p class="text-sm text-gray-500">Parcourez les catégories, marques et modèles d'équipements</p>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

                <!-- ========================================================= -->
                <!-- 1) COLONNE — Catégories -->
                <!-- ========================================================= -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <!-- Header -->
                    <div class="px-5 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                            </svg>
                            Catégories
                        </h3>
                    </div>

                    <!-- Search Input -->
                    <div class="px-4 py-3 bg-white border-b border-gray-100">
                        <div class="relative">
                            <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                            <input 
                                v-model="searchCategory"
                                type="text" 
                                placeholder="Rechercher..." 
                                class="w-full pl-9 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-shadow"
                            >
                            <button 
                                v-if="searchCategory"
                                @click="searchCategory = ''"
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="p-3 max-h-[500px] overflow-y-auto custom-scrollbar">
                        <!-- Loading State -->
                        <template v-if="loadingCategories">
                            <div class="py-12 flex flex-col items-center justify-center">
                                <div class="w-8 h-8 border-2 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                                <p class="text-xs text-gray-500 mt-3">Chargement...</p>
                            </div>
                        </template>

                        <!-- Categories List -->
                        <template v-else-if="filteredCategories.length > 0">
                            <div 
                                v-for="cat in filteredCategories"
                                :key="cat"
                                @click="selectCategorie(cat)"
                                class="px-3 py-2.5 mb-1 rounded-md text-sm cursor-pointer transition-colors"
                                :class="selectedCategorie === cat 
                                    ? 'bg-red-50 text-red-700 font-medium border border-red-200' 
                                    : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                                "
                            >
                                <img :src="cat.icon" alt="" class="w-4 h-4 inline-block mr-2">
                                {{ cat.label }}
                            </div>
                        </template>

                        <!-- Empty State -->
                        <template v-else>
                            <div class="py-12 text-center">
                                <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <p class="text-sm text-gray-500">Aucune catégorie trouvée</p>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- ========================================================= -->
                <!-- 2) COLONNE — Marques -->
                <!-- ========================================================= -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <!-- Header -->
                    <div class="px-5 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                            </svg>
                            Marques
                        </h3>
                    </div>

                    <!-- Search Input -->
                    <div class="px-4 py-3 bg-white border-b border-gray-100">
                        <div class="relative">
                            <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                            <input 
                                v-model="searchMarque"
                                type="text" 
                                placeholder="Rechercher..." 
                                :disabled="!selectedCategorie"
                                class="w-full pl-9 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-shadow disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                            <button 
                                v-if="searchMarque"
                                @click="searchMarque = ''"
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="p-3 max-h-[500px] overflow-y-auto custom-scrollbar">
                        <!-- Placeholder State -->
                        <template v-if="!selectedCategorie">
                            <div class="py-12 text-center">
                                <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/>
                                </svg>
                                <p class="text-sm text-gray-500 mb-1">Sélectionnez une catégorie</p>
                                <p class="text-xs text-gray-400">pour afficher les marques</p>
                            </div>
                        </template>

                        <!-- Loading State -->
                        <template v-else-if="loadingMarques">
                            <div class="py-12 flex flex-col items-center justify-center">
                                <div class="w-8 h-8 border-2 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                                <p class="text-xs text-gray-500 mt-3">Chargement...</p>
                            </div>
                        </template>

                        <!-- Marques List -->
                        <template v-else-if="filteredMarques.length > 0">
                            <div 
                                v-for="marque in filteredMarques"
                                :key="marque"
                                @click="selectMarque(marque)"
                                class="px-3 py-2.5 mb-1 rounded-md text-sm cursor-pointer transition-colors"
                                :class="selectedMarque === marque 
                                    ? 'bg-red-50 text-red-700 font-medium border border-red-200' 
                                    : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                                "
                            >
                                {{ marque }}
                            </div>
                        </template>

                        <!-- Empty State -->
                        <template v-else>
                            <div class="py-12 text-center">
                                <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <p class="text-sm text-gray-500">Aucune marque trouvée</p>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- ========================================================= -->
                <!-- 3) COLONNE — Modèles -->
                <!-- ========================================================= -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <!-- Header -->
                    <div class="px-5 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            Modèles
                        </h3>
                    </div>

                    <!-- Search Input -->
                    <div class="px-4 py-3 bg-white border-b border-gray-100">
                        <div class="relative">
                            <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                            <input 
                                v-model="searchModel"
                                type="text" 
                                placeholder="Rechercher..." 
                                :disabled="!selectedMarque"
                                class="w-full pl-9 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-shadow disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                            <button 
                                v-if="searchModel"
                                @click="searchModel = ''"
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Contact Fournisseur -->
                    <div v-if="fournisseurTel && fournisseurTel != '-1' && !loadingModeles" class="px-3">
                        <div class="bg-blue-50 p-3 border border-blue-200 rounded-md mb-4">
                            <p class="text-xs font-medium text-blue-900 mb-0.5">Contact fournisseur</p>
                            <p class="text-sm font-semibold text-blue-700"><i class="fas fa-tag mr-1"></i> {{ fournisseurTel.marque }}</p>
                            <p class="text-sm font-semibold text-blue-700"><i class="fas fa-phone mr-1"></i> {{ fournisseurTel.tel }}</p>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="p-3 max-h-[500px] overflow-y-auto custom-scrollbar">
                        <!-- Placeholder State -->
                        <template v-if="!selectedMarque">
                            <div class="py-12 text-center">
                                <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/>
                                </svg>
                                <p class="text-sm text-gray-500 mb-1">Sélectionnez une marque</p>
                                <p class="text-xs text-gray-400">pour afficher les modèles</p>
                            </div>
                        </template>

                        <!-- Loading State -->
                        <template v-else-if="loadingModeles">
                            <div class="py-12 flex flex-col items-center justify-center">
                                <div class="w-8 h-8 border-2 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                                <p class="text-xs text-gray-500 mt-3">Chargement...</p>
                            </div>
                        </template>

                        <!-- Modeles List -->
                        <template v-else-if="filteredModeles.length > 0">
                            <div 
                                v-for="model in filteredModeles"
                                :key="model.reference_sku"
                                class="bg-white hover:bg-gray-50 border border-gray-200 rounded-md p-3 mb-2 transition-colors"
                            >
                                <div class="font-medium text-sm text-gray-900 mb-1">
                                    {{ model.modele }}
                                </div>
                                
                                <div class="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                                    </svg>
                                    <span>{{ model.reference_sku }}</span>
                                </div>

                                <div class="flex items-center gap-2">
                                    <!-- Photo -->
                                    <a 
                                        v-if="model.photo_url"
                                        :href="model.photo_url"
                                        target="_blank"
                                        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors"
                                    >
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                        </svg>
                                        <span>Photo</span>
                                    </a>

                                    <!-- Doc -->
                                    <a 
                                        v-if="model.doc_url"
                                        :href="model.doc_url"
                                        target="_blank"
                                        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors"
                                    >
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                        <span>Doc</span>
                                    </a>
                                </div>
                            </div>
                        </template>

                        <!-- Empty State -->
                        <template v-else>
                            <div class="py-12 text-center">
                                <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <p class="text-sm text-gray-500">Aucun modèle trouvé</p>
                            </div>
                        </template>
                    </div>
                </div>

            </div>
        </app-template>
    </div>
</template>

<script>
export default {
    data() {
        return {
            // Loading states
            loadingCategories: false,
            loadingMarques: false,
            loadingModeles: false,

            // Data
            categories: [],
            marques: [],
            modeles: [],

            // Selected items
            selectedCategorie: null,
            selectedMarque: null,

            // Search terms
            searchCategory: '',
            searchMarque: '',
            searchModel: '',

            // Fournisseur contact
            fournisseurTel: null,
        }
    },

    computed: {
        filteredCategories() {
            if (!this.searchCategory) return this.categories;
            const search = this.searchCategory.toLowerCase();
            return this.categories.filter(cat => 
                cat.toLowerCase().includes(search)
            );
        },

        filteredMarques() {
            if (!this.searchMarque) return this.marques;
            const search = this.searchMarque.toLowerCase();
            return this.marques.filter(marque => 
                marque.toLowerCase().includes(search)
            );
        },

        filteredModeles() {
            if (!this.searchModel) return this.modeles;
            const search = this.searchModel.toLowerCase();
            return this.modeles.filter(model => 
                model.modele.toLowerCase().includes(search) ||
                model.reference_sku.toLowerCase().includes(search)
            );
        }
    },

    methods: {
        async getCategories() {
            this.loadingCategories = true;
            try {
                let response = await this.$request.post('/reference/get-categories', {});
                this.categories = response || [];
            } catch (e) {
                console.error(e);
                this.$toast.error('Erreur lors du chargement des catégories');
            }
            this.loadingCategories = false;
        },

        async getMarques(categorie) {
            this.loadingMarques = true;
            this.marques = [];
            this.modeles = [];
            this.selectedMarque = null;
            this.searchMarque = '';
            this.searchModel = '';

            try {
                let response = await this.$request.post('/reference/get-marques', {
                    categorie: categorie
                });
                this.marques = response || [];
            } catch (e) {
                console.error(e);
                this.$toast.error('Erreur lors du chargement des marques');
            }
            this.loadingMarques = false;
        },

        async getModeles(categorie, marque) {
            this.loadingModeles = true;
            this.modeles = [];
            this.searchModel = '';

            try {
                let response = await this.$request.post('/reference/get-modeles', {
                    categorie: categorie,
                    marque: marque
                });
                this.modeles = response || [];

                let responseTel = await this.$request.post('/reference/fournisseur-tel', {
                    marque: marque
                });
                this.fournisseurTel = responseTel;
            } catch (e) {
                console.error(e);
                this.$toast.error('Erreur lors du chargement des modèles');
            }
            this.loadingModeles = false;
        },

        selectCategorie(cat) {
            this.selectedCategorie = cat;
            this.getMarques(cat);
        },

        selectMarque(marque) {
            this.selectedMarque = marque;
            this.getModeles(this.selectedCategorie, marque);
        }
    },

    mounted() {
        this.getCategories();
    }
}
</script>

<style scoped>
/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: #f9fafb;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}
</style>
